import { createAreaDimension } from '@/editor/data/dimension/dimension';
import { AreaDimensionModel, IAreaDimensionModelOptions } from '@/editor/entities/2d/areaDimensionModel';
import { MeasureAbsorbManager } from '@/editor/manager/absorb/measure/measureAbsorbManager';
import { thresholdValue } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { TGlobalStores } from '@/editor/stores';
import { EToolType } from '@/editor/types';
import { clientPosToWorldPos } from '@/editor/utils';
import { CommonController2d } from '../CommonController';
import { isSimplePolygon } from '@/editor/math/utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { VirtualAreaDimensionTextInfo } from '@/editor/entities/2d/textInfo/virtualAreaDimensionTextInfo';

const AREA_DIMENSION_VIRTUAL_ID = 'areaDimensionVirtual';

export class DrawAreaDimension extends CommonController2d {
  private readonly _mouseButton = { LEFT: 0, RIGHT: 2 } as const;
  private _points: IPoint2d[] = [];
  private _rectStartPoint: IPoint2d | null = null;
  private _movePoint: IPoint2d | null = null;
  private _absorbManager: MeasureAbsorbManager;
  private _areaDimensionVirtualModel = new AreaDimensionModel({ id: AREA_DIMENSION_VIRTUAL_ID, points: [] });
  private _virtualTextInfo = new VirtualAreaDimensionTextInfo();
  private _allowEnterToComplete: boolean = false;

  constructor(gs: TGlobalStores) {
    super(gs);
    this._absorbManager = new MeasureAbsorbManager(gs);
    this._absorbManager.updateBaseData({ areaDimension: true });

    document.addEventListener('keypress', this._keyPressHandler);
  }

  private _keyPressHandler = (ev: KeyboardEvent) => {
    if (!(ev.target instanceof HTMLBodyElement)) return;

    if (ev.key === 'Enter') {
      if (!this._isRectMode && this._allowEnterToComplete) {
        this._complete();
      }
    }
  };

  protected override mouseDown(_e: MouseEvent): void {}

  private get _isRectMode() {
    return this._gs.operateInfo.toolType === EToolType.AreaDimRect;
  }

  private _getWorldPositionFromMouseEvent(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;

    return clientPosToWorldPos(e, camera, renderer);
  }

  private _getAbsorbPoint(e: MouseEvent) {
    const rawPoint = this._getWorldPositionFromMouseEvent(e);
    const absorbPoint = this._absorbManager.calculateAbsorbPointThenRender(rawPoint);

    return { rawPoint, point: absorbPoint ?? rawPoint };
  }

  private _getConnectedStartPoint(point: IPoint2d) {
    if (this._points.length < 3) return null;

    const startPoint = this._points[0];
    if (Point2dUtils.distanceTo(point, startPoint) > this._gs.dynamicAbsorbDistance) return null;

    return startPoint;
  }

  private _getFreeModeLegalPoint(e: MouseEvent) {
    const { rawPoint, point } = this._getAbsorbPoint(e);

    return this._getConnectedStartPoint(rawPoint) ?? this._getConnectedStartPoint(point) ?? point;
  }

  private _getRectModeLegalPoint(e: MouseEvent) {
    return this._getAbsorbPoint(e).point;
  }

  private _pushPoint(point: IPoint2d) {
    const lastPoint = this._points[this._points.length - 1];
    if (lastPoint && Point2dUtils.equals(lastPoint, point)) return;

    this._points.push(Point2dUtils.clone(point));
    if (isInsertInPoints(this._points)) {
      this._points.pop();
      return;
    }
    this._absorbManager.setOrthogonalData(point);

    if (!this._isRectMode) {
      this._gs.dimensionOperation.addEndpoint(point, !lastPoint ? 'highlight' : 'normal');
    }
  }

  private _rollbackPoint() {
    const removedPoint = this._points.pop();
    const currentPoint = this._points[this._points.length - 1];

    this._absorbManager.clearModel();
    if (currentPoint) {
      this._absorbManager.setOrthogonalData(currentPoint);
    } else {
      this._absorbManager.clearOrthogonalData();
    }

    return removedPoint;
  }

  private _updateVirtualModel(points: IPoint2d[], options: IAreaDimensionModelOptions = {}) {
    this._areaDimensionVirtualModel.updateModel(
      {
        id: AREA_DIMENSION_VIRTUAL_ID,
        points
      },
      options
    );
  }

  private _updateFreeVirtualModel() {
    const points = this._points.map((point) => Point2dUtils.clone(point));

    if (this._movePoint && points.length) {
      const lastPoint = points[points.length - 1];
      const closeToStart = points.length >= 3 && Point2dUtils.equals(this._movePoint, points[0]);

      if (!lastPoint || !Point2dUtils.equals(lastPoint, this._movePoint)) {
        if (!closeToStart) {
          points.push(Point2dUtils.clone(this._movePoint));
        }
      }
    }

    const closed = !!this._movePoint && points.length >= 3 && Point2dUtils.equals(this._movePoint, points[0]);
    this._updateVirtualModel(points, { closed, fill: closed, checkValid: !closed });

    if (!closed && isSimplePolygon(this._points)) {
      this._areaDimensionVirtualModel.updateMask(this._points);
      this._allowEnterToComplete = true;
    } else {
      this._allowEnterToComplete = false;
      this._areaDimensionVirtualModel.clearMask();
    }
  }

  private _complete() {
    if (this._points.length < 3) return;

    this._gs.dimensionOperation.addAreaDimension(createAreaDimension(this._points, this._isRectMode));
    this.dispose();
    this.exit();
  }

  private _getRectPoints(start: IPoint2d, end: IPoint2d) {
    return [Point2dUtils.clone(start), { x: end.x, y: start.y }, Point2dUtils.clone(end), { x: start.x, y: end.y }];
  }

  private _isLegalRect(start: IPoint2d, end: IPoint2d) {
    return Math.abs(start.x - end.x) > thresholdValue && Math.abs(start.y - end.y) > thresholdValue;
  }

  private _updateRectVirtualModel(start: IPoint2d, end: IPoint2d) {
    if (!this._isLegalRect(start, end)) {
      this._updateVirtualModel(Point2dUtils.equals(start, end) ? [Point2dUtils.clone(start)] : [start, end], {
        closed: false,
        fill: false
      });
      return;
    }

    const temporarilyRectPoints = this._getRectPoints(start, end);
    this._updateVirtualModel(temporarilyRectPoints, { closed: true, fill: true });
    this._virtualTextInfo.update(temporarilyRectPoints);
  }

  private _clickRectModeLeftButton(point: IPoint2d) {
    if (!this._rectStartPoint) {
      this._rectStartPoint = Point2dUtils.clone(point);
      this._absorbManager.setOrthogonalData(point);
      this._updateRectVirtualModel(this._rectStartPoint, point);
      this._gs.dimensionOperation.addEndpoint(point);
      return;
    }

    if (!this._isLegalRect(this._rectStartPoint, point)) return;

    this._gs.dimensionOperation.addAreaDimension(
      createAreaDimension(this._getRectPoints(this._rectStartPoint, point), this._isRectMode)
    );
    this.dispose();
    this.exit();
  }

  private _clickRectModeRightButton() {
    if (this._rectStartPoint) {
      this._rectStartPoint = null;
      this._movePoint = null;
      this._areaDimensionVirtualModel.clear();
      this._virtualTextInfo.clear();
      this._absorbManager.clearOrthogonalData();
      this._absorbManager.clearModel();
      this._gs.dimensionOperation.removeAllEndPoints();
      return;
    }

    this.dispose();
    this.exit();
  }

  private _clickFreeModeLeftButton(point: IPoint2d) {
    const connectStartPoint = this._getConnectedStartPoint(point);

    if (connectStartPoint) {
      if (isSimplePolygon(this._points)) this._complete();
    } else {
      this._pushPoint(point);
      this._updateFreeVirtualModel();
    }
  }

  private _clickFreeModeRightButton() {
    const removedPoint = this._rollbackPoint();
    if (removedPoint) {
      this._updateFreeVirtualModel();
      this._gs.dimensionOperation.removeEndPoint(removedPoint);
      return;
    }

    this.dispose();
    this.exit();
  }

  protected override mouseMove(e: MouseEvent): void {
    this._movePoint = this._isRectMode ? this._getRectModeLegalPoint(e) : this._getFreeModeLegalPoint(e);

    if (this._isRectMode && this._rectStartPoint) {
      this._updateRectVirtualModel(this._rectStartPoint, this._movePoint);
    } else if (!this._isRectMode) {
      this._updateFreeVirtualModel();
    }
  }

  protected override mouseUp(e: MouseEvent, dragged: boolean): void {
    if (dragged) return;

    if (e.button === this._mouseButton.LEFT) {
      if (this._isRectMode) {
        this._clickRectModeLeftButton(this._movePoint ?? this._getRectModeLegalPoint(e));
      } else {
        this._clickFreeModeLeftButton(this._movePoint ?? this._getFreeModeLegalPoint(e));
      }

      return;
    }

    if (e.button === this._mouseButton.RIGHT) {
      if (this._isRectMode) {
        this._clickRectModeRightButton();
      } else {
        this._clickFreeModeRightButton();
      }

      return;
    }
  }

  clear() {
    this._points = [];
    this._rectStartPoint = null;
    this._movePoint = null;
    this._allowEnterToComplete = false;
    this._areaDimensionVirtualModel.clear();
    this._areaDimensionVirtualModel.clearMask();
    this._virtualTextInfo.clear();
    this._absorbManager.clearOrthogonalData();
    this._absorbManager.clearModel();
  }

  override dispose(): void {
    document.removeEventListener('keypress', this._keyPressHandler);
    this.clear();
    this._areaDimensionVirtualModel.dispose();
    this._virtualTextInfo.dispose();
    this._absorbManager.dispose();
    this._gs.dimensionOperation.removeAllEndPoints();
    super.dispose();
  }
}

function isInsertInPoints(points: IPoint2d[]) {
  if (points.length < 4) {
    return false;
  }

  const segments: LineSeg2d[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push(new LineSeg2d(points[i], points[i + 1]));
  }

  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      // 相邻线段共享端点，不视为相交
      if (j === i + 1) continue;

      if (segments[i].intersection(segments[j])) {
        return true;
      }
    }
  }

  return false;
}
